import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { style } from './zui-shell-user-css.js';
import { ZuiShellBase } from './zui-shell-base';
import { ifDefined } from 'lit/directives/if-defined.js';
import '@zywave/zui-icons';

/**
 * Topbar feature that encompasses information about the User Principle, User Profile, impersonation, and notifications.
 *
 * @element zui-shell-user
 *
 * @event switchprofile - An event that indicates a user is requesting to switch profile
 *
 * @csspart login - The login link; this is exposed as a CSS shadow part and can be accessed with `::part(login)`
 * @csspart userprofile - The user profile button; this is exposed as a CSS shadow part and can be accessed with `::part(userprofile)`
 *
 * @cssprop [--zui-shell-user-avatar-background=var(--zui-gray)] - Background color of the avatar when user principle initials are used to represent the current logged in user
 * @cssprop [--zui-shell-user-avatar=#fff] - Icon/text color of the principal user initials in the avatar
 * @cssprop [--zui-shell-profile-avatar-background=var(--zui-blue)] - Background color of the icon that represents the profile in context
 * @cssprop [--zui-shell-profile-avatar-color=white] - Icon/text color of the profile in context
 */
export class ZuiShellUser extends ZuiShellBase {
  /**
   * The first name of the user. It should be set to the `given_name` claim of the user principal. This may not yet be available on some principals, but will be back filled with profile information by an upcoming auth release. This component gracefully falls back to username.
   */
  @property({ type: String, attribute: 'given-name' }) givenName = '';

  /**
   * The last name of the user. It should be set to the `family_name` claim of the user principal. This may not yet be available on some principals, but will be back filled with profile information by an upcoming auth release. This component gracefully falls back to username.
   */
  @property({ type: String, attribute: 'family-name' }) familyName = '';

  /**
   * This is the display name of the profile in context and will vary from profile type to profile type.
   */
  @property({ type: String, attribute: 'profile-name' }) profileName = '';

  /**
   * This is additional profile information displayed as subtext. It should generally represent the location and/or profile name. You may want to make this a function to ensure a clean rendering with the assumption of missing claims.
   */
  @property({ type: String, attribute: 'profile-detail' }) profileDetail = '';

  /**
   * This is the username of the user. It should be set to the `preferred_username` claim of the user principal.
   */
  @property({ type: String, attribute: 'username' }) username = '';

  /**
   * This is a URL to user self service tool. It should be set to `https://profiles.zywave.com` per environment.
   */
  @property({ type: String, attribute: 'manage-user-url' }) manageUserUrl = '';

  /**
   * This is a URL to the logout page of the application.
   */
  @property({ type: String, attribute: 'logout-user-url' }) logoutUserUrl: string | null = null;

  /**
   * This is a URL to the proper edit page for the profile in context.
   */
  @property({ type: String, attribute: 'manage-profile-url' }) manageProfileUrl: string | null = null;

  /**
   * This is a URL to the local switch profile path. **This should only be set if the user has multiple contextual profiles available.**
   */
  @property({ type: String, attribute: 'switch-profile-url' }) switchProfileUrl: string | null = null;

  /**
   * This indicates that the user has multiple profiles, and should be presented UI to help navigate that feature (this will need to be set at a future date)
   */
  @property({ type: Boolean, attribute: 'has-multiple-profiles' }) hasMultipleProfiles = false;

  /**
   * This is a URI of the avatar of the user. It should be set to the "picture" claim of the user principal.
   */
  @property({ type: String, attribute: 'avatar' }) avatar: string | null = null;

  /**
   * This is the text that appears above the user section. Should be set to localized "User".
   */
  @property({ type: String, attribute: 'user-header' }) userHeader = 'User';

  /**
   * This is the text that appears above the profile section. Should be set to localized phrase representing the profile type such as "Agency Profile", "Employer Profile" or just simply "Profile".
   */
  @property({ type: String, attribute: 'profile-header' }) profileHeader = 'Profile';

  /**
   * This is the text that appears as the manage user link. Should be set to localized "Manage User".
   */
  @property({ type: String, attribute: 'manage-user-text' }) manageUserText = 'Manage user';

  /**
   * This is the text that appears as the logout user link. Should be set to localized "Logout".
   */
  @property({ type: String, attribute: 'logout-user-text' }) logoutUserText = 'Log out';

  /**
   * This is the text that appears as the manage profile link. Should be set to localized "Manage Profile".
   */
  @property({ type: String, attribute: 'manage-profile-text' }) manageProfileText = 'Manage profile';

  /**
   * This is the text that appears as the switch profile link. Should be set to localized "Switch".
   */
  @property({ type: String, attribute: 'switch-profile-text' }) switchProfileText = 'Switch';

  /**
   * Boolean for when the user dropdown is open.
   */
  @property({ type: Boolean, reflect: true }) open = false;

  /**
   * This is the how many unread notifications the user/profile has
   */
  @property({ type: Number, attribute: 'notification-count' }) notificationCount = 0;

  /**
   * This is the text that appears above the notification section.
   */
  @property({ type: String, attribute: 'notification-header' }) notificationHeader = 'Notifications';

  /**
   * This is the text that appears after the notification count in profile section.
   */
  @property({ type: String, attribute: 'notification-title' }) viewNotificationTitle = 'New notifications';

  /**
   * This is the text that appears when there are no new notifications in the notifications section of the user dropdown.
   */
  @property({ type: String, attribute: 'no-notification-title' }) noNotificationTitle = 'No new notifications';

  /**
   * This is the text that appears as the notification inbox link.
   */
  @property({ type: String, attribute: 'view-notifications-text' }) viewNotificationText = 'View all';

  /**
   * This is a URL to the notification inbox (ex: `dev-zh.zywave.com/notifications/inbox`).
   */
  @property({ type: String, attribute: 'view-notifications-url' }) viewNotificationUrl = '';

  /**
   * This property is required for all the impersonation properties to work and show.
   */
  @property({ type: Boolean }) impersonating = false;

  /**
   * Text for the current user impersonating another profile.
   */
  @property({ type: String, attribute: 'impersonating-action' }) impersonatingAction = 'acting as';

  /**
   * Name of the profile the current user is impersonating.
   */
  @property({ type: String, attribute: 'impersonating-profile-name' }) impersonatingProfileName = '';

  /**
   * First name of the user associated with the profile the current user is impersonating.
   */
  @property({ type: String, attribute: 'impersonating-given-name' }) impersonatingGivenName = '';

  /**
   * Last name of the user associated with the profile the current user is impersonating.
   */
  @property({ type: String, attribute: 'impersonating-family-name' }) impersonatingFamilyName = '';

  /**
   * Indicates that the user is currently unauthenticated, and should be given access to login.
   */
  @property({ type: Boolean }) unauthenticated = false;

  /**
   * This is the text that displays to unauthenticated users to login.
   */
  @property({ type: String, attribute: 'login-user-text' }) loginUserText = 'Log in';

  /**
   * The url where unauthenticated users can be redirected to login.
   */
  @property({ type: String, attribute: 'login-user-url' }) loginUserUrl = 'https://auth.zywave.com/';

  /**
   * Display name of the current user principal
   * @default [firstName lastName | username]
   */
  get displayName(): string {
    const { givenName, familyName, username } = this;
    let displayName = `${givenName} ${familyName}`.trim();
    if (!displayName) {
      displayName = username;
    }
    return displayName;
  }

  /**
   * Initials of the current user principal
   */
  get initials(): string {
    const { givenName, familyName } = this;
    let initials = '';
    if (givenName) {
      initials += givenName[0];
    }
    if (familyName) {
      initials += familyName[0];
    }
    return initials.trim();
  }

  /**
   * Display name of the user profile being impersonated
   */
  get impersonatingDisplayName(): string {
    const { impersonatingGivenName, impersonatingFamilyName, impersonatingProfileName } = this;
    let impersonatingDisplayName = `${impersonatingGivenName} ${impersonatingFamilyName}`.trim();
    if (!impersonatingDisplayName) {
      impersonatingDisplayName = impersonatingProfileName;
    }
    return impersonatingDisplayName;
  }

  static get styles() {
    return [super.styles, style];
  }

  get #formattedNotificationCount() {
    let notificationCount: string | number = this.notificationCount;
    if (notificationCount > 99) {
      notificationCount = '99+';
    }
    return notificationCount;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('click', (e) => {
      if (!e.composedPath().includes(this)) {
        this.open = false;
      }
    });
  }

  render() {
    if (this.unauthenticated) {
      return html`
        <div class="unauthenticated-user">
          <a part="login" class="login-user-link" href="${this.loginUserUrl}">${this.loginUserText}</a>
        </div>
      `;
    }
    return html`
      <button
        part="userprofile"
        class="user-profile"
        aria-label="Toggle user profile dropdown"
        @click="${this.#openUserProfile}"
      >
        ${this.#getNotificationCountHtml()}
        <div class="user-profile-info">
          <div class="display-name">${this.displayName}</div>
          ${this.#getProfileHtml()}
        </div>
        ${this.#getAvatarHtml()}
      </button>
      ${this.#getSubmenuHtml()}
    `;
  }

  #getProfileHtml() {
    const { impersonating, impersonatingAction, impersonatingDisplayName, profileName } = this;

    return html`
      ${impersonating
        ? html` <div class="profile-name impersonating-as">${impersonatingAction} ${impersonatingDisplayName}</div> `
        : html` <div class="profile-name">${profileName}</div> `}
    `;
  }

  #getNotificationCountHtml() {
    if (this.#formattedNotificationCount) {
      return html`
        <span title="${this.notificationCount}" class="notification-count"> ${this.#formattedNotificationCount} </span>
      `;
    }
  }

  #openUserProfile() {
    this.open = !this.open;
  }

  #getAvatarHtml() {
    const { avatar, initials, displayName, impersonating } = this;

    let avatarHtml = html` <div class="avatar-icon"><zui-icon icon="zui-user" class="small"></zui-icon></div> `;

    if (impersonating) {
      avatarHtml = html` <div class="avatar-image"><zui-icon icon="zui-shell-impersonation"></zui-icon></div> `;
    } else {
      if (avatar) {
        avatarHtml = html` <div class="avatar-image"><img src="${avatar}" alt="${displayName}" /></div> `;
      } else if (initials) {
        avatarHtml = html` <div class="avatar-initials">${initials}</div> `;
      }
    }
    return html` <div class="user-avatar ${impersonating ? 'impersonating' : ''}"> ${avatarHtml} </div> `;
  }

  #getSubmenuAvatarHtml() {
    const { avatar, initials, displayName } = this;

    let avatarHtml = html` <div class="avatar-icon"><zui-icon icon="zui-user" class="small"></zui-icon></div> `;

    if (avatar) {
      avatarHtml = html` <div class="avatar-image"><img src="${avatar}" alt="${displayName}" /></div> `;
    } else if (initials) {
      avatarHtml = html` <div class="avatar-initials">${initials}</div> `;
    }

    return html` <div class="user-avatar"> ${avatarHtml} </div> `;
  }

  #getSubmenuHtml() {
    return html`
      <div class="user-profile-submenu">
        ${this.#getSubmenuUserHtml()} ${this.#getSubmenuFeatureFlagsHtml()} ${this.#getSubmenuProfileHtml()}
        ${this.#getSubmenuNotificationHtml()}
      </div>
    `;
  }

  #getSubmenuNotificationHtml() {
    const {
      notificationHeader,
      viewNotificationText,
      viewNotificationTitle,
      viewNotificationUrl,
      noNotificationTitle,
    } = this;
    if (!viewNotificationUrl) {
      return html``;
    }
    return html`
      <div class="header"> ${notificationHeader} </div>
      <div class="notification-details">
        ${this.#formattedNotificationCount
          ? html`
              <div title="${this.notificationCount}" class="notification-count"
                >${this.#formattedNotificationCount}</div
              >
              <div class="notification-title">${viewNotificationTitle}</div>
            `
          : html` <div class="notification-title">${noNotificationTitle}</div> `}
        <a href="${viewNotificationUrl}">${viewNotificationText}</a>
      </div>
    `;
  }

  #getSubmenuUserHtml() {
    const { userHeader, displayName, username, manageUserUrl, manageUserText, logoutUserUrl, logoutUserText } = this;
    if (!displayName) {
      return html``;
    }
    return html`
      <div class="header">${userHeader}</div>
      <div class="user-details">
        ${this.#getSubmenuAvatarHtml()}
        <div class="user-info">
          <div class="display-name">${displayName}</div>
          <div class="username">${username !== displayName ? username : ''}</div>
        </div>
      </div>
      ${manageUserUrl || logoutUserUrl
        ? html`
            <div class="actions">
              ${manageUserUrl ? html` <a href="${manageUserUrl}">${manageUserText}</a> ` : ''}
              ${logoutUserUrl ? html` <a class="right" href="${logoutUserUrl}">${logoutUserText}</a> ` : ''}
            </div>
          `
        : ''}
    `;
  }

  #getSubmenuFeatureFlagsHtml() {
    return html`<div class="feature-flags"><slot name="feature-flags"></slot></div>`;
  }

  #getSubmenuProfileHtml() {
    const {
      profileHeader,
      profileName,
      profileDetail,
      manageProfileUrl,
      manageProfileText,
      switchProfileUrl,
      switchProfileText,
      impersonating,
      impersonatingAction,
      impersonatingDisplayName,
      hasMultipleProfiles,
    } = this;
    if (!profileName) {
      return html``;
    }
    return html`
      <div class="header"
        >${profileHeader}
        ${impersonating ? html` <span class="impersonating-header">(${impersonatingAction})</span> ` : ''}</div
      >
      <div class="profile-details">
        <div class="profile-avatar ${impersonating ? 'impersonating' : ''}">
          ${impersonating
            ? html` <zui-icon icon="zui-shell-impersonation" class="small"></zui-icon> `
            : html` <zui-icon icon="zui-building" class="small"></zui-icon> `}
        </div>
        <div class="profile-info">
          ${impersonating
            ? html`
                <div class="profile-name">${impersonatingDisplayName}</div>
                <div class="profile-detail">${profileName}</div>
                <div class="profile-detail">${profileDetail !== profileName ? profileDetail : ''}</div>
              `
            : html`
                <div class="profile-name">${profileName}</div>
                <div class="profile-detail">${profileDetail !== profileName ? profileDetail : ''}</div>
              `}
        </div>
      </div>
      ${manageProfileUrl || switchProfileUrl || hasMultipleProfiles
        ? html`
            <div class="actions">
              ${manageProfileUrl ? html` <a href="${manageProfileUrl}">${manageProfileText}</a> ` : ''}
              ${switchProfileUrl || hasMultipleProfiles
                ? html`
                    <a
                      class="right"
                      href="${ifDefined(switchProfileUrl ?? undefined)}"
                      @click="${this.#onSwitchProfile}"
                      >${switchProfileText}</a
                    >
                  `
                : ''}
            </div>
          `
        : ''}
    `;
  }

  #onSwitchProfile() {
    if (!this.switchProfileUrl) {
      this.dispatchEvent(new CustomEvent('switchprofile', { bubbles: true }));
    }
  }
}

window.customElements.define('zui-shell-user', ZuiShellUser);

declare global {
  interface HTMLElementTagNameMap {
    'zui-shell-user': ZuiShellUser;
  }
}
